import { useState } from "react";
import {
  Banknote,
  CheckCircle2,
  Loader2,
  Pencil,
  Trash2,
  Wallet,
} from "lucide-react";
import {
  accountTypeLabelEs,
  getUruguayBank,
  type UruguayBankAccountType,
} from "@gestionalo/shared-types";

import { BankAccountForm } from "@/components/shared/BankAccountForm";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { ProfessionalBankAccountDto } from "@/services/professionalEarningsService";

interface BankAccountSectionProps {
  account:
    | (ProfessionalBankAccountDto & { updatedAt?: string })
    | null
    | undefined;
  isLoading: boolean;
  isSubmitting: boolean;
  isDeleting: boolean;
  onSubmit: (body: ProfessionalBankAccountDto) => Promise<unknown> | void;
  onDelete: () => Promise<unknown> | void;
}

export function BankAccountSection({
  account,
  isLoading,
  isSubmitting,
  isDeleting,
  onSubmit,
  onDelete,
}: BankAccountSectionProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isLoading && !account) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
        Cargando datos bancarios…
      </div>
    );
  }

  // No account yet and not editing → empty state
  if (!account && !isEditing) {
    return (
      <div className="flex flex-col items-start gap-4 rounded-lg border border-dashed bg-background/40 p-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Banknote className="h-5 w-5" aria-hidden />
        </div>
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-foreground">
            Aún no cargaste una cuenta bancaria
          </h3>
          <p className="text-sm text-muted-foreground">
            Configurá los datos donde querés recibir tus extracciones. Podés
            cambiarla cuando quieras.
          </p>
        </div>
        <Button type="button" onClick={() => setIsEditing(true)}>
          Configurar cuenta
        </Button>
      </div>
    );
  }

  // Editing mode (either configuring for first time or updating)
  if (isEditing) {
    return (
      <BankAccountForm
        initial={account ?? null}
        isSubmitting={isSubmitting}
        onCancel={account ? () => setIsEditing(false) : undefined}
        onSubmit={async (body) => {
          await onSubmit(body);
          setIsEditing(false);
        }}
      />
    );
  }

  // Read-only summary
  const bank = getUruguayBank(account!.institutionCode);
  return (
    <div className="rounded-lg border bg-background/40">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Wallet className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-foreground">
                {bank?.shortName ?? account!.institutionCode}
              </h3>
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-500/15 px-2.5 py-0.5 text-xs font-semibold text-emerald-300">
                <CheckCircle2 className="h-3 w-3" aria-hidden />
                Configurada
              </span>
            </div>
            {bank ? (
              <p className="text-xs text-muted-foreground">
                {bank.officialName} · Código BCU {bank.code}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="mr-2 h-4 w-4" aria-hidden />
            Editar
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isDeleting}
            onClick={() => setConfirmOpen(true)}
            className={cn(
              "border-destructive/40 bg-transparent text-destructive",
              "hover:border-destructive hover:!bg-destructive/15 hover:!text-destructive",
              "focus-visible:ring-destructive",
            )}
          >
            {isDeleting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="mr-2 h-4 w-4" aria-hidden />
            )}
            Eliminar
          </Button>
        </div>
      </div>

      <dl className="grid gap-x-8 gap-y-4 p-5 sm:grid-cols-2">
        <Field label="Titular" value={account!.holderName} />
        <Field
          label="Documento del titular"
          value={account!.holderDocumentId}
        />
        <Field label="Número de cuenta" value={account!.accountNumber} mono />
        <Field
          label="Tipo de cuenta"
          value={
            account!.accountType
              ? accountTypeLabelEs(account!.accountType as UruguayBankAccountType)
              : "—"
          }
        />
      </dl>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar cuenta bancaria</DialogTitle>
            <DialogDescription>
              Vas a tener que volver a cargarla para solicitar una extracción.
              Esta acción no afecta pagos ya realizados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isDeleting}
              onClick={async () => {
                await onDelete();
                setConfirmOpen(false);
              }}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Eliminando…
                </>
              ) : (
                "Eliminar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          "text-sm text-foreground",
          mono && "font-mono tracking-wide",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
